import { runtimeConfig } from '../config/runtime';
import {
  OAuthCallbackData,
  OAuthProvider,
  OAuthTokens,
  OAuthUserInfo,
  PKCECodes,
} from '../types/oauth';
import { oauthServiceConsentAdapter } from './privacyAdapters';

// Re-export for backward compatibility
export type { OAuthCallbackData, OAuthProvider, OAuthTokens, OAuthUserInfo };

class OAuthService {
  private providers: Record<string, OAuthProvider> = {};
  private initialized = false;

  private initializeProviders() {
    const baseUrl = window.location.origin;

    // Google OAuth
    this.providers.google = {
      id: 'google',
      name: 'Google',
      color: '#DB4437',
      icon: 'G',
      clientId: runtimeConfig.get('GOOGLE_CLIENT_ID'),
      authorizationEndpoint: 'https://accounts.google.com/o/oauth2/v2/auth',
      tokenEndpoint: 'https://oauth2.googleapis.com/token',
      scope: 'openid email profile',
      redirectUri: `${baseUrl}/auth/callback/google`,
    };

    // Facebook OAuth
    this.providers.facebook = {
      id: 'facebook',
      name: 'Facebook',
      color: '#4267B2',
      icon: 'f',
      clientId: runtimeConfig.get('FACEBOOK_CLIENT_ID'),
      authorizationEndpoint: 'https://www.facebook.com/v18.0/dialog/oauth',
      tokenEndpoint: 'https://graph.facebook.com/v18.0/oauth/access_token',
      scope: 'email public_profile',
      redirectUri: `${baseUrl}/auth/callback/facebook`,
    };

    // Microsoft OAuth
    this.providers.microsoft = {
      id: 'microsoft',
      name: 'Microsoft',
      color: '#0078D4',
      icon: 'M',
      clientId: runtimeConfig.get('MICROSOFT_CLIENT_ID'),
      authorizationEndpoint:
        'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
      tokenEndpoint:
        'https://login.microsoftonline.com/common/oauth2/v2.0/token',
      scope: 'openid email profile',
      redirectUri: `${baseUrl}/auth/callback/microsoft`,
    };

    // GitHub OAuth
    this.providers.github = {
      id: 'github',
      name: 'GitHub',
      color: '#333333',
      icon: 'G',
      clientId: runtimeConfig.get('GITHUB_CLIENT_ID'),
      authorizationEndpoint: 'https://github.com/login/oauth/authorize',
      tokenEndpoint: 'https://github.com/login/oauth/access_token',
      scope: 'user:email',
      redirectUri: `${baseUrl}/auth/callback/github`,
    };

    this.initialized = true;
  }

  private ensureInitialized() {
    if (!this.initialized) {
      this.initializeProviders();
    }
  }

  // Method to reinitialize providers when runtime config is updated
  reinitialize() {
    this.initialized = false;
    this.ensureInitialized();
  }

  // PKCE utility methods
  private generateRandomString(length: number): string {
    const charset =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return result;
  }

  private async sha256(plain: string): Promise<ArrayBuffer> {
    const encoder = new TextEncoder();
    const data = encoder.encode(plain);
    return window.crypto.subtle.digest('SHA-256', data);
  }

  private base64URLEncode(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    bytes.forEach(byte => (binary += String.fromCharCode(byte)));
    return btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  private async generatePKCECodes(): Promise<PKCECodes> {
    const codeVerifier = this.generateRandomString(128);
    const codeChallenge = this.base64URLEncode(await this.sha256(codeVerifier));

    return {
      codeVerifier,
      codeChallenge,
    };
  }

  getProvider(providerId: string): OAuthProvider | undefined {
    this.ensureInitialized();
    return this.providers[providerId];
  }

  getAvailableProviders(): OAuthProvider[] {
    this.ensureInitialized();
    return Object.values(this.providers).filter(provider => provider.clientId);
  }

  /** Cookie options - path and SameSite ensure cookie survives OAuth redirect */
  private getCookieOptions() {
    return { expires: 1, path: '/', sameSite: 'lax' as const };
  }

  /** Encode state with returnUrl so it survives the OAuth redirect (avoids cookie persistence issues) */
  private encodeState(nonce: string, returnUrl: string): string {
    const payload = JSON.stringify({ n: nonce, r: returnUrl || '/' });
    return btoa(payload)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  /** Extract returnUrl from state param (fallback when cookie is lost) */
  getReturnUrlFromState(stateParam: string | null): string | undefined {
    if (!stateParam) return undefined;
    try {
      const padded = stateParam.replace(/-/g, '+').replace(/_/g, '/');
      const json = atob(padded);
      const parsed = JSON.parse(json) as { n?: string; r?: string };
      return typeof parsed?.r === 'string' ? parsed.r : undefined;
    } catch {
      return undefined;
    }
  }

  async initiateLogin(providerId: string, returnUrl?: string): Promise<void> {
    this.ensureInitialized();
    const provider = this.providers[providerId];

    if (!provider || !provider.clientId) {
      throw new Error(`OAuth provider ${providerId} not configured`);
    }

    const resolvedReturnUrl = returnUrl || '/';
    const cookieOpts = this.getCookieOptions();

    // Store return URL in cookie (primary) - use explicit path/sameSite for redirect persistence
    oauthServiceConsentAdapter.setCookie(
      `oauth_return_url_${providerId}`,
      resolvedReturnUrl,
      cookieOpts
    );

    // Store provider ID for callback handling
    oauthServiceConsentAdapter.setCookie(
      'oauth_provider',
      providerId,
      cookieOpts
    );

    try {
      // Generate PKCE codes
      const pkceCodes = await this.generatePKCECodes();
      const nonce = this.generateRandomString(32);
      const stateParam = this.encodeState(nonce, resolvedReturnUrl);

      // Store code verifier for later use
      oauthServiceConsentAdapter.setCookie(
        `oauth_code_verifier_${providerId}`,
        pkceCodes.codeVerifier,
        cookieOpts
      );

      // Build authorization URL - state includes returnUrl so it survives redirect
      const params = new URLSearchParams({
        response_type: 'code',
        client_id: provider.clientId,
        redirect_uri: provider.redirectUri,
        scope: provider.scope,
        code_challenge: pkceCodes.codeChallenge,
        code_challenge_method: 'S256',
        state: stateParam,
      });

      // Provider-specific parameters
      if (providerId === 'google') {
        params.set('access_type', 'offline');
        params.set('prompt', 'consent');
      } else if (providerId === 'microsoft') {
        params.set('prompt', 'consent');
      }

      // Store state for validation (must match what we send)
      oauthServiceConsentAdapter.setCookie(
        `oauth_state_${providerId}`,
        stateParam,
        cookieOpts
      );

      // Redirect to authorization endpoint
      const authUrl = `${provider.authorizationEndpoint}?${params.toString()}`;
      window.location.href = authUrl;
    } catch (error) {
      throw new Error(`Failed to initiate ${provider.name} login`);
    }
  }

  // Get callback data for backend processing (fixes CORS issue)
  getCallbackData(): OAuthCallbackData | null {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');

    if (error) {
      throw new Error(`OAuth error: ${error}`);
    }

    if (!code) {
      return null;
    }

    const providerId = oauthServiceConsentAdapter.getCookie('oauth_provider');

    if (!providerId) {
      throw new Error('No OAuth provider found in callback');
    }

    // Validate state
    const storedState = oauthServiceConsentAdapter.getCookie(
      `oauth_state_${providerId}`
    );
    if (state !== storedState) {
      throw new Error('Invalid state parameter');
    }

    // Get stored code verifier
    const codeVerifier = oauthServiceConsentAdapter.getCookie(
      `oauth_code_verifier_${providerId}`
    );
    if (!codeVerifier) {
      throw new Error('Code verifier not found');
    }

    return {
      provider: providerId,
      code,
      state: state || '',
      codeVerifier,
    };
  }

  private async fetchGoogleUserInfo(
    accessToken: string
  ): Promise<OAuthUserInfo> {
    const response = await fetch(
      'https://www.googleapis.com/oauth2/v2/userinfo',
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch Google user info');
    }

    const data = await response.json();

    return {
      id: data.id,
      email: data.email,
      name: data.name,
      firstName: data.given_name,
      lastName: data.family_name,
      picture: data.picture,
      provider: 'google',
    };
  }

  private async fetchFacebookUserInfo(
    accessToken: string
  ): Promise<OAuthUserInfo> {
    const response = await fetch(
      `https://graph.facebook.com/me?fields=id,name,email,first_name,last_name,picture&access_token=${accessToken}`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch Facebook user info');
    }

    const data = await response.json();

    return {
      id: data.id,
      email: data.email,
      name: data.name,
      firstName: data.first_name,
      lastName: data.last_name,
      picture: data.picture?.data?.url,
      provider: 'facebook',
    };
  }

  private async fetchMicrosoftUserInfo(
    accessToken: string
  ): Promise<OAuthUserInfo> {
    const response = await fetch('https://graph.microsoft.com/v1.0/me', {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch Microsoft user info');
    }

    const data = await response.json();

    return {
      id: data.id,
      email: data.mail || data.userPrincipalName,
      name: data.displayName,
      firstName: data.givenName,
      lastName: data.surname,
      provider: 'microsoft',
    };
  }

  private async fetchGitHubUserInfo(
    accessToken: string
  ): Promise<OAuthUserInfo> {
    const [userResponse, emailResponse] = await Promise.all([
      fetch('https://api.github.com/user', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }),
      fetch('https://api.github.com/user/emails', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }),
    ]);

    if (!userResponse.ok || !emailResponse.ok) {
      throw new Error('Failed to fetch GitHub user info');
    }

    const [userData, emailData] = await Promise.all([
      userResponse.json(),
      emailResponse.json(),
    ]);

    const primaryEmail =
      emailData.find((email: any) => email.primary)?.email ||
      emailData[0]?.email;
    const nameParts = userData.name?.split(' ') || [];

    return {
      id: userData.id.toString(),
      email: primaryEmail,
      name: userData.name || userData.login,
      firstName: nameParts[0] || userData.login,
      lastName: nameParts.slice(1).join(' ') || '',
      picture: userData.avatar_url,
      provider: 'github',
    };
  }

  getReturnUrl(providerId: string): string | undefined {
    const returnUrl = oauthServiceConsentAdapter.getCookie(
      `oauth_return_url_${providerId}`
    );
    if (returnUrl) {
      oauthServiceConsentAdapter.removeCookie(`oauth_return_url_${providerId}`);
    }
    return returnUrl;
  }

  clearTokens(providerId: string): void {
    // Clear cookies
    oauthServiceConsentAdapter.removeCookie('oauth_provider');
    oauthServiceConsentAdapter.removeCookie(`oauth_return_url_${providerId}`);
    oauthServiceConsentAdapter.removeCookie(
      `oauth_code_verifier_${providerId}`
    );
    oauthServiceConsentAdapter.removeCookie(`oauth_state_${providerId}`);
  }

  isProviderConfigured(providerId: string): boolean {
    this.ensureInitialized();
    return !!this.providers[providerId]?.clientId;
  }

  // Utility method to check if we're in an OAuth callback
  static isOAuthCallback(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('code') || urlParams.has('error');
  }

  // Clean up after successful OAuth flow
  cleanup(): void {
    const providerId = oauthServiceConsentAdapter.getCookie('oauth_provider');
    if (providerId) {
      this.clearTokens(providerId);
    }
  }
}

export const oauthService = new OAuthService();
export default oauthService;
